import { Button, Form, Input, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../../app/store";
import ArrowOutIcon from "../../../../../assets/icons/ArrowOutIcon";
import ArrowSquareIcon from "../../../../../assets/icons/ArrowSquareIcon";
import CaretDownIcon from "../../../../../assets/icons/CaretDownIcon";
import Loader from "../../../../../components/ui/Loader";
import { useGetAllFeaturesQuery } from "../../../../../features/superAdmin/ticketService/feature/feature.api";
import { useGetAllProductsQuery } from "../../../../../features/superAdmin/ticketService/product/product.api";
import {
  useGetAllTemplatesQuery,
  useGetTemplateDetailsQuery,
} from "../../../../../features/superAdmin/ticketService/template/template.api";
import useModal from "../../../../../hooks/useModal";
import useRoleBasedPath from "../../../../../hooks/useRoleBasedPath";
import { capitalize } from "../../../../../utils/string";
import { formatSLA } from "../../../../../utils/utils";
import WorkFlows from "../../_components/WorkFlows";
import { TEMPLATE_TYPES } from "../../constants/ticket.constants";
import AddCCModal from "./AddCCModal";
import ContentFromTemplate from "./ContentsFromTemplate";
type Props = { onClose?: () => void };
const ManageTicketForm = ({ onClose }: Props) => {
  const user = useAppSelector((state) => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();
  const { open, handleOpen, handleClose } = useModal<unknown, "CONFIRM">();
  const [form] = Form.useForm();
  const { ticketPath } = useRoleBasedPath();
  // Component states
  const [uploading, setUploading] = useState(false);

  // Component refs
  type AccordionSectionRef = {
    handleRaiseTicket: () => any;
  };
  const accordionRef = useRef<AccordionSectionRef>(null);

  // Watched values
  const selectedTemplateId = Form.useWatch("template", form);
  const selectedProduct = Form.useWatch("product_id", form);

  // Queries
  const { data: templateData } = useGetAllTemplatesQuery({
    params: {
      page: 1,
      limit: 100000,
      status: "active",
    },
  });

  const { data: productData, isLoading: isProductLoading } =
    useGetAllProductsQuery({
      params: {
        page: 1,
        limit: 100000,
        status: "active",
      },
    });

  const {
    data: featureData,
    isLoading: isFeatureLoading,
    isFetching: isFeatureFetching,
  } = useGetAllFeaturesQuery(
    {
      params: {
        page: 1,
        limit: 100000,
        status: "active",
        product_id: selectedProduct,
      },
    },
    {
      skip: !selectedProduct,
    },
  );

  const templateOptions = templateData?.data?.tickets?.map((t) => ({
    value: t?.id,
    label: (
      <div className="flex justify-between items-center group">
        <span>{t?.name}</span>
        <Link
          onClick={(e) => {
            e.stopPropagation();
          }}
          target="_blank"
          to={`${ticketPath}/all-templates/template-details/${t?.id}`}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-gray-400"
        >
          <ArrowSquareIcon />
        </Link>
      </div>
    ),
    searchLabel: t?.name,
  }));
  const productOptions = productData?.data?.products?.map((p) => ({
    value: p.id,
    label: p.title,
  }));
  const featureOptions = featureData?.data?.features?.map((p) => ({
    value: p.id,
    label: p.title,
  }));

  const {
    data: template,
    isLoading: templateLoading,
    isFetching: templateFetching,
  } = useGetTemplateDetailsQuery(selectedTemplateId, {
    skip: !selectedTemplateId,
  });
  const templateDetails = template?.data;
  const workflowsData = templateDetails?.ticketRoles;
  const fileUploadFieldsData = templateDetails?.requiredFields?.filter(
    (field) => field.required_field_type === "file_upload",
  );
  const othersFieldsData = templateDetails?.requiredFields?.filter(
    (field) => field.required_field_type !== "file_upload",
  );
  const isSlaTemplate =
    templateDetails?.type === TEMPLATE_TYPES.sla && selectedTemplateId;

  const handleSubmit = (values: any) => {
    if (accordionRef.current) {
      const data = accordionRef?.current?.handleRaiseTicket();
      const updateData = {
        raised_by: user?.id,
        ...data,
        ...values,
      };
      handleOpen({
        type: "CONFIRM",
        data: updateData,
      });
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(`${ticketPath}/all-tickets`);
    }
  };

  const handleNavigating = async () => {
    try {
      const values = await form.getFieldsValue();
      localStorage.setItem("ticket-contents", JSON.stringify(values));

      navigate(`${ticketPath}/all-tickets/create-new`);
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  useEffect(() => {
    const savedForm = localStorage.getItem("ticket-contents");

    if (savedForm) {
      const parsedForm = JSON.parse(savedForm);
      form.setFieldsValue(parsedForm);
      localStorage.removeItem("ticket-contents");
    }
  }, [form]);

  const renderTemplateContents = () => {
    if (!selectedTemplateId) return;

    if (templateLoading || templateFetching) {
      return (
        <div className="min-h-60 flex flex-col justify-center">
          <Loader />
        </div>
      );
    }

    return (
      <div className="border border-light rounded-lg p-4 pb-0">
        <div className="flex justify-between pb-7 mb-7 border-b border-b-light">
          <div>
            <span className="text-base">Category: </span>
            <span className="text-lg">
              {capitalize(templateDetails?.category as string)}
            </span>
          </div>
          <div>
            <span className="text-base">SLA: </span>
            <span className="text-lg">
              {formatSLA(templateDetails?.totalSLA)}
            </span>
          </div>
        </div>
        <ContentFromTemplate
          ref={accordionRef}
          othersFieldsData={othersFieldsData}
          fileUploadFieldsData={fileUploadFieldsData}
          setUploading={setUploading}
        />
        <WorkFlows
          isSlaBased={templateDetails?.type === TEMPLATE_TYPES.sla}
          workflowsData={workflowsData}
        />
      </div>
    );
  };

  return (
    <div>
      {open?.type === "CONFIRM" && (
        <AddCCModal
          data={open.data}
          templateId={selectedTemplateId}
          open={open.open}
          handleParentClose={onClose}
          handleClose={() => {
            handleClose();
          }}
        />
      )}
      <div className="flex ice justify-between pb-3 mb-7 border-b border-b-light">
        <h3 className="text-[22px] text-[#1C222B] font-medium">
          Create Ticket
        </h3>
        {!(
          location.pathname === `${ticketPath}/all-tickets/create-new` ||
          location.pathname === "/user/ticket-management/all-tickets/create-new"
        ) && (
          <button onClick={handleNavigating} type="button">
            <button className="text-status-dark-text">
              <ArrowOutIcon />
            </button>
          </button>
        )}
      </div>
      <Form
        requiredMark={false}
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <div className="space-y-4">
          <Form.Item
            label="Template"
            name="template"
            rules={[{ required: true, message: "Please select a template" }]}
          >
            <Select
              size="large"
              suffixIcon={<CaretDownIcon />}
              placeholder="Select template"
              allowClear
              showSearch
              onChange={() => {
                form.setFieldsValue({
                  severity: undefined,
                  priority: undefined,
                });
              }}
              filterOption={(input, option) =>
                (option?.searchLabel ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={templateOptions}
            />
          </Form.Item>

          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please enter the title" }]}
          >
            <Input size="large" placeholder="Title" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: "Please enter the description" },
            ]}
          >
            <Input.TextArea size="large" placeholder="Description" />
          </Form.Item>

          <Form.Item
            label="Priority"
            name="priority"
            rules={[{ required: true, message: "Please select a priority" }]}
          >
            <Select
              allowClear
              size="large"
              suffixIcon={<CaretDownIcon />}
              placeholder="Select priority"
              options={priorities}
            />
          </Form.Item>

          {isSlaTemplate ? (
            <Form.Item
              label="Severity"
              name="severity"
              rules={[{ required: true, message: "Please select a severity" }]}
            >
              <Select
                allowClear
                size="large"
                suffixIcon={<CaretDownIcon />}
                placeholder="Select severity"
                options={severities}
              />
            </Form.Item>
          ) : null}

          {templateDetails?.category === "product" ? (
            <>
              <Form.Item
                label="Product"
                name="product_id"
                rules={[{ required: true, message: "Please select a product" }]}
              >
                <Select
                  allowClear
                  showSearch
                  size="large"
                  suffixIcon={<CaretDownIcon />}
                  placeholder="Select product"
                  optionFilterProp="label"
                  options={productOptions}
                  disabled={isProductLoading}
                  onChange={() => form.resetFields(["feature_id"])}
                />
              </Form.Item>
              <Form.Item
                label="Feature"
                name="feature_id"
                rules={[{ required: true, message: "Please select a feature" }]}
              >
                <Select
                  allowClear
                  showSearch
                  size="large"
                  suffixIcon={<CaretDownIcon />}
                  placeholder="Select feature"
                  optionFilterProp="label"
                  options={featureOptions}
                  disabled={
                    !selectedProduct ||
                    isProductLoading ||
                    isFeatureFetching ||
                    isFeatureLoading
                  }
                />
              </Form.Item>
            </>
          ) : null}

          {renderTemplateContents()}

          <div className="flex justify-between items-center">
            <Button
              onClick={handleCancel}
              className="button-bordered-light h-10 !min-w-[140px]"
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              disabled={uploading}
              className="button-primary-light h-10 !min-w-[140px]"
            >
              Continue
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

const priorities = [
  {
    value: "critical",
    label: "P1",
  },
  {
    value: "major",
    label: "P2",
  },
  {
    value: "moderate",
    label: "P3",
  },
  {
    value: "minor",
    label: "P4",
  },
];
const severities = [
  {
    value: "s1",
    label: "S1",
  },
  {
    value: "s2",
    label: "S2",
  },
  {
    value: "s3",
    label: "S3",
  },
  {
    value: "s4",
    label: "S4",
  },
];

export default ManageTicketForm;
